import { reportError } from '@/sentry';

type Check = (value: unknown) => boolean;

export class ShapeError extends Error {
  constructor(public what: string) {
    super(`Unexpected ${what} from the server`);
    this.name = 'ShapeError';
  }
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

export const str: Check = (v) => typeof v === 'string';
export const num: Check = (v) => typeof v === 'number' && Number.isFinite(v);
export const bool: Check = (v) => typeof v === 'boolean';
export const nullable =
  (check: Check): Check =>
  (v) =>
    v === null || check(v);
export const oneOf =
  (...allowed: string[]): Check =>
  (v) =>
    typeof v === 'string' && allowed.includes(v);
export const arrayOf =
  (check: Check): Check =>
  (v) =>
    Array.isArray(v) && v.every(check);
export const shape =
  (fields: Record<string, Check>): Check =>
  (v) =>
    isRecord(v) && Object.entries(fields).every(([key, check]) => check(v[key]));

export function checked<T>(check: Check, what: string): (value: unknown) => T {
  return (value) => {
    if (check(value)) return value as T;
    const error = new ShapeError(what);
    reportError(error, { what });
    throw error;
  };
}

const answer = shape({ answer_index: num, is_correct: bool });

export const QUESTION = shape({ id: str, topic: num, answers: arrayOf(answer) });

export const QUESTION_LIST = shape({ data: arrayOf(QUESTION), count: num });

export const BILET = shape({ number: num, questions: arrayOf(QUESTION) });

export const BANK = shape({
  data: arrayOf(str),
  count: num,
  position: num,
  questions: arrayOf(QUESTION),
});

export const PRACTICE_SESSION = shape({
  id: str,
  label: str,
  position: num,
  questions: arrayOf(QUESTION),
});

const examQuestion = shape({
  position: num,
  question_id: str,
  topic: num,
  answers: arrayOf(shape({ answer_index: num })),
  selected_answer_index: nullable(num),
  correct_answer_index: nullable(num),
  is_correct: nullable(bool),
});

export const EXAM_SESSION = shape({
  id: str,
  status: oneOf('in_progress', 'submitted', 'expired'),
  question_count: num,
  time_limit_seconds: num,
  pass_threshold: num,
  started_at: str,
  expires_at: str,
  submitted_at: nullable(str),
  score_correct: nullable(num),
  passed: nullable(bool),
  questions: arrayOf(examQuestion),
});
